import React from "react";
import { Promobanner as ContractPromobanner } from "~/use-cases/contracts/Promobanner";
import { RichTextRenderer } from "~/ui/components/rich-text-renderer";

type Promobanner = ContractPromobanner & {
  style?: React.CSSProperties;
  className?: string;
};

type PromoBannerConfig = {
  id: string;
  fromDate: string;
  toDate: string;
  message: string;
  theme?: keyof typeof themes;
  className?: string;
  style?: React.CSSProperties;
  show?: boolean;
};

type PromoBannerProps = {
  promobanner: Promobanner; // will contain 0 or 1 items
};

const themes = {
  default: "linear-gradient(90deg, #e0e0e0, #ffffff, #e0e0e0)",
  festive: "linear-gradient(90deg, #ffecd2, #fcb69f)",
  party: "linear-gradient(90deg, #f5576c, #f093fb)",
  confetti: "linear-gradient(90deg, #43e97b, #38f9d7)",
  norway: "linear-gradient(90deg, #e7a7a7, #f3f3f3, #a7b8d8)",
  independence: "linear-gradient(90deg, #ff4e50, #f9d423)",
  patriotic: "linear-gradient(90deg, #0033cc, #ffffff, #ff0000)",
  spring: "linear-gradient(90deg, #a1ffce, #faffd1)",
  summer: "linear-gradient(90deg, #f6d365, #fda085)",
  autumn: "linear-gradient(90deg, #fbc2eb, #a6c1ee)",
  winter: "linear-gradient(90deg, #e0c3fc, #8ec5fc)",
  sale: "linear-gradient(90deg, #ff6a00, #ee0979)",
  blackfriday: "linear-gradient(90deg, #000000, #434343)",
  luxury: "linear-gradient(90deg, #bdc3c7, #2c3e50)",
  calm: "linear-gradient(90deg, #d4fc79, #96e6a1)",
  neutral: "linear-gradient(90deg, #f8f9fa, #e9ecef)",
  sky: "linear-gradient(90deg, #a1c4fd, #c2e9fb)",
} as const;

export default function PromoBanner({ promobanner }: PromoBannerProps) {
  if (!promobanner?.show) return null;

  const now = new Date();
  const from = promobanner.fromDate ? new Date(promobanner.fromDate) : null;
  const to = promobanner.toDate ? new Date(promobanner.toDate) : null;
  if (to && !Number.isNaN(to.getTime())) {
    to.setHours(23, 59, 59, 999); // full end day
  }

  const fromOk = !from || Number.isNaN(from.getTime()) || now >= from;
  const toOk = !to || Number.isNaN(to.getTime()) || now <= to;
  if (!fromOk || !toOk) return null;

  const themeKey = (promobanner.theme ?? "default") as keyof typeof themes;
  const background = themes[themeKey] ?? themes.default;

  return (
    <div
      className={`flex flex-auto items-center mx-auto px-6 pt-3 pb-2 text-black ${promobanner.className ?? ""}`}
      style={{ background, ...promobanner.style }}
    >
      <div className="text-center w-full">
        <RichTextRenderer json={promobanner.message.json} />
      </div>
    </div>
  );
}
